import React from 'react';
import { Box, Chip } from '@mui/material';
import { StayDetail, StayCompound } from '../../services/restserver-openapi';

type StayStatusChipProps = {
    bookableCompoundType?: StayCompound.bookableCompound;
    stayStatus?: StayDetail.stayStatus;
    statusSource?: StayDetail.statusSource;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

type StatusPresentation = {
    label: string;
    color: 'default' | 'success' | 'error' | 'warning' | 'info';
    dotColor: string;
}

const STAY_STATUS = {
    PLANNED: 'PLANNED' as StayDetail.stayStatus,
    REQUEST_SENT: 'REQUEST_SENT' as StayDetail.stayStatus,
    CONFIRMED: 'CONFIRMED' as StayDetail.stayStatus,
    DECLINED: 'DECLINED' as StayDetail.stayStatus,
    NO_RESPONSE: 'NO_RESPONSE' as StayDetail.stayStatus,
    CANCELLED: 'CANCELLED' as StayDetail.stayStatus,
    CHANGED: 'CHANGED' as StayDetail.stayStatus,
} as const;

const STATUS_SOURCE = {
    APP: 'APP' as StayDetail.statusSource,
} as const;

const getPlannedLabel = (bookableCompoundType?: StayCompound.bookableCompound): string => {
    if (bookableCompoundType === StayCompound.bookableCompound.DIRECT) {
        return 'Direkt buchbar';
    }
    if (bookableCompoundType === StayCompound.bookableCompound.REQUEST) {
        return 'Anfrage noetig';
    }
    if (bookableCompoundType === StayCompound.bookableCompound.NONE) {
        return 'Auf gut Glueck';
    }
    return 'Geplant';
};

const getStatusPresentation = (
    stayStatus?: StayDetail.stayStatus,
    bookableCompoundType?: StayCompound.bookableCompound
): StatusPresentation => {
    switch (stayStatus) {
        case STAY_STATUS.CONFIRMED:
            return { label: 'Bestaetigt', color: 'success', dotColor: '#2e7d32' };
        case STAY_STATUS.DECLINED:
            return { label: 'Abgelehnt', color: 'error', dotColor: '#d32f2f' };
        case STAY_STATUS.CANCELLED:
            return { label: 'Gelöscht', color: 'warning', dotColor: '#ed6c02' };
        case STAY_STATUS.NO_RESPONSE:
            return { label: 'Keine Rueckmeldung', color: 'default', dotColor: '#6b7280' };
        case STAY_STATUS.REQUEST_SENT:
            return { label: 'Anfrage gesendet', color: 'info', dotColor: '#0288d1' };
        case STAY_STATUS.CHANGED:
            return { label: 'Geaendert', color: 'warning', dotColor: '#ed6c02' };
        case STAY_STATUS.PLANNED:
        default:
            return { label: getPlannedLabel(bookableCompoundType), color: 'default', dotColor: '#64748b' };
    }
};

const StayStatusChip: React.FC<StayStatusChipProps> = ({ bookableCompoundType, stayStatus, statusSource, onClick }) => {
    const presentation = getStatusPresentation(stayStatus, bookableCompoundType);
    const isViaApp = stayStatus === STAY_STATUS.CONFIRMED && statusSource === STATUS_SOURCE.APP;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
                size="small"
                color={presentation.color}
                onClick={onClick}
                clickable={!!onClick}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: presentation.dotColor,
                            }}
                        />
                        {presentation.label}
                    </Box>
                }
                sx={onClick ? { cursor: 'pointer' } : undefined}
            />
            {isViaApp && <Chip size="small" variant="outlined" color="success" label="via App" />}
        </Box>
    );
};

export default StayStatusChip;
